import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Meeting } from '../../../models/meeting';
import { UserService } from '../../../services/user.service';

export interface DenyMeetingDialogData {
  meeting: Meeting;
}

/** Result is only present when the caller confirms the deny (vs. canceling out). */
export interface DenyMeetingDialogResult {
  note?: string;
}

/**
 * Collects an optional reason before denying a meeting request or declining
 * a proposal — shared by the counselor's pending-requests list and the
 * student's accept/decline UI, since a proposal can now come from either side.
 */
@Component({
  selector: 'app-deny-meeting-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './deny-meeting-dialog.html',
  styleUrl: './deny-meeting-dialog.css',
})
export class DenyMeetingDialogComponent {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  protected dialogRef = inject(MatDialogRef<DenyMeetingDialogComponent, DenyMeetingDialogResult>);
  protected data = inject<DenyMeetingDialogData>(MAT_DIALOG_DATA);

  protected noteControl = this.fb.control('');

  protected get defaultMessage(): string {
    return this.data.meeting.isChangeRequest
      ? 'Proposed changes declined. Original meeting still scheduled'
      : 'Meeting denied';
  }

  /** The reason is read by the other participant. */
  protected get reasonPlaceholder(): string {
    const isFaculty = this.userService.currentUser?.id === this.data.meeting.facultyUserId;
    return isFaculty ? 'Let the student know why…' : 'Let your counselor know why…';
  }

  confirm(): void {
    const note = this.noteControl.value?.trim() || undefined;
    this.dialogRef.close({ note });
  }
}
